import { useRedirectUrlHandler } from '@/composables/useRedirectUrlHandler'
import {
  COMPLETE_SIGN_IN_RESET_DELAY_MS,
  POST_SIGNOUT_REDIRECT_URL_KEY,
  POST_SIGNOUT_ROUTE_KEY,
} from '@/composables/auth/constants'
import { consumeCustomState, serializeCustomState } from '@/composables/auth/customState'
import { persistRememberedSignInEmailAfterSignIn } from '@/composables/auth/rememberedSignInEmail'
import { clearBackendSession } from '@/api/session'
import {
  beginAuthRedirect,
  clearAuthSession,
  completeAuthRedirect,
  initializeAuthClients,
  signOutFromProvider,
} from '@/composables/auth/oidc'
import type { AuthPrompt, AuthRedirectOptions, User } from '@/composables/auth/types'
import { getAccessToken, getUser } from '@/composables/auth/user'
import { router } from '@/plugins/router'
import { Routes } from '@/plugins/router/constants'
import { settingsService } from '@/services/settingsService'
import type { RouteLocationRaw } from 'vue-router'
import { useIdentityStore } from '@/stores/identityStore'
import { useErrorStore } from '@/stores/errorStore'

export type { User, CustomState } from '@/composables/auth/types'

let isCompletingSignIn = false

export function useAuth() {
  const configure = () => {
    return initializeAuthClients()
  }

  const setPostSignoutRoute = (options?: { route?: RouteLocationRaw; redirectUrl?: string }) => {
    try {
      let route = options?.route ?? router.currentRoute.value
      const resolvedRoute = route ? router.resolve(route) : null
      if (
        resolvedRoute?.name === Routes.Logout ||
        resolvedRoute?.name === Routes.Callback ||
        resolvedRoute?.name === Routes.SignoutCallback
      ) {
        route = { name: Routes.Login }
      }

      if (route) {
        localStorage.setItem(POST_SIGNOUT_ROUTE_KEY, JSON.stringify(route))
      }

      if (options?.redirectUrl) {
        localStorage.setItem(POST_SIGNOUT_REDIRECT_URL_KEY, options.redirectUrl)
      } else {
        localStorage.removeItem(POST_SIGNOUT_REDIRECT_URL_KEY)
      }
    } catch (err: any) {
      console.warn(`failed to set post_signout_route: ${err.message}`)
    }
  }

  const signOut = async (options?: { route?: RouteLocationRaw; redirectUrl?: string }) => {
    setPostSignoutRoute(options)
    useIdentityStore().clearRedirectUrl()

    try {
      await clearBackendSession()
    } catch (error) {
      console.error('[SignOut] Failed to clear backend session:', error)
    }

    await signOutFromProvider()
  }

  const postSignoutRedirect = async () => {
    useIdentityStore().clearRedirectUrl()
    const externalRedirectUrl = localStorage.getItem(POST_SIGNOUT_REDIRECT_URL_KEY)
    if (externalRedirectUrl) {
      localStorage.removeItem(POST_SIGNOUT_REDIRECT_URL_KEY)

      if (useRedirectUrlHandler().isValidRedirectUrl(externalRedirectUrl)) {
        window.location.href = externalRedirectUrl
        return
      }
    }

    let route: RouteLocationRaw = { name: Routes.Login }
    const fromLocalStorage = localStorage.getItem(POST_SIGNOUT_ROUTE_KEY)
    if (fromLocalStorage) {
      route = JSON.parse(fromLocalStorage) as RouteLocationRaw
      localStorage.removeItem(POST_SIGNOUT_ROUTE_KEY)
    }

    const resolvedRoute = router.resolve(route)
    if (
      !resolvedRoute.matched.length ||
      resolvedRoute.name === Routes.Logout ||
      resolvedRoute.name === Routes.Callback ||
      resolvedRoute.name === Routes.SignoutCallback
    ) {
      route = { name: Routes.Login }
    }

    await router.push(route)
  }

  const clearSession = () => {
    clearAuthSession()
  }

  const buildCustomState = () =>
    serializeCustomState({
      redirectUrl: useIdentityStore().input.redirectUrl,
    })

  const startWorkforceSignIn = async (emailAddress?: string) => {
    await beginAuthRedirect('workforce', {
      customState: buildCustomState(),
      emailAddress,
    })
  }

  const isFlowEnabled = (flow: Parameters<typeof settingsService.isFlowEnabled>[0]) =>
    settingsService.isFlowEnabled(flow)

  const startExternalCodeLogin = async (
    emailAddress?: string,
    options?: Pick<AuthRedirectOptions, 'prompt'>,
  ) => {
    await beginAuthRedirect('external-signin', {
      customState: buildCustomState(),
      emailAddress,
      prompt: options?.prompt,
    })
  }

  const startExternalCodeSignup = async (emailAddress?: string) => {
    await beginAuthRedirect('external-signup', {
      customState: buildCustomState(),
      emailAddress,
      prompt: 'create',
    })
  }

  const startExternalPasswordLogin = async (
    emailAddress?: string,
    options?: { prompt?: AuthPrompt },
  ) => {
    await beginAuthRedirect('external-signin-password', {
      customState: buildCustomState(),
      emailAddress,
      prompt: options?.prompt,
    })
  }

  const startExternalGoogleSignIn = async () => {
    // Prefer the dedicated external password client when present because
    // customer flows can be attached to a different app registration.
    const socialFlow = settingsService.isFlowEnabled('external-signin-password')
      ? 'external-signin-password'
      : 'external-signin'

    await beginAuthRedirect(socialFlow, {
      customState: buildCustomState(),
      identityProvider: 'Google',
    })
  }

  const startExternalMicrosoftSignIn = async () => {
    const socialFlow = settingsService.isFlowEnabled('external-signin-password')
      ? 'external-signin-password'
      : 'external-signin'

    await beginAuthRedirect(socialFlow, {
      customState: buildCustomState(),
      identityProvider: 'daa0af10-5214-426d-94e3-dd7465c90273',
    })
  }

  const startExternalPasswordSignup = async (emailAddress?: string) => {
    await beginAuthRedirect('external-signup-password', {
      customState: buildCustomState(),
      emailAddress,
      prompt: 'create',
    })
  }

  /**
   * Home-tenant / work-school sign-in via the External ID authority (`idp` query param).
   * Pass the IdP resource id from IAM `entra-external-signin-config` or
   * `VITE_ENTRA_EXTERNAL_AZUREAD_IDP` (resolved in the UI).
   *
   * Does not send MSAL `login_hint`: CIAM can route `login_hint` to local email / OTP before
   * honoring federated `idp`; domain allowlists are still enforced client-side before redirect.
   */
  const startExternalOrganizationSignIn = async (options: { idp: string }) => {
    const socialFlow = settingsService.isFlowEnabled('external-signin-password')
      ? 'external-signin-password'
      : 'external-signin'

    await beginAuthRedirect(socialFlow, {
      customState: buildCustomState(),
      identityProvider: options.idp,
    })
  }

  const completeSignIn = async () => {
    if (isCompletingSignIn) {
      return
    }

    isCompletingSignIn = true

    try {
      const authResult = await completeAuthRedirect()

      const customState = consumeCustomState(authResult?.state)
      if (customState?.redirectUrl) {
        useIdentityStore().setRedirectUrl(customState.redirectUrl)
      }

      const user: User = await getUser()

      if (user.isAuthenticated) {
        persistRememberedSignInEmailAfterSignIn(user.emailAddress)
        useIdentityStore().userCreationPending = true
        await useRedirectUrlHandler().redirect()
      } else {
        await router.push(Routes.Login)
      }
    } catch (error: any) {
      const errorStore = useErrorStore()
      const message = error?.message || 'Failed to complete sign-in. Please try again.'
      errorStore.addError(message)
      console.error('[CompleteSignIn] Error:', error)
      await router.push(Routes.Login)
    } finally {
      setTimeout(() => {
        isCompletingSignIn = false
      }, COMPLETE_SIGN_IN_RESET_DELAY_MS)
    }
  }

  return {
    configure,
    getUser,
    getAccessToken,
    serializeCustomState,
    completeRedirectSignIn: completeSignIn,
    signOut,
    setPostSignoutRoute,
    clearSession,
    postSignoutRedirect,
    isFlowEnabled,
    startWorkforceSignIn,
    startExternalCodeLogin,
    startExternalCodeSignup,
    startExternalGoogleSignIn,
    startExternalMicrosoftSignIn,
    startExternalPasswordLogin,
    startExternalPasswordSignup,
    startExternalOrganizationSignIn,
  }
}
